import { inject, injectable } from 'tsyringe';
import type {
    ProviderConnection,
    CreateBody,
    UpdateBody,
    StartBody,
    CallbackBody,
    ReauthStartBody,
} from '@bmai/connections-api-client';
import type { ProviderConnectionValidationResult, ProviderType } from '@bmai/shared';
import {
    AppBasePathToken,
    AuthFetchToken,
    ConnectionsApiBasePathToken,
    ConnectionsClientToken,
    type AuthFetch,
    type ConnectionsClient,
} from '@/container/IntegrationTokens';

export interface ProviderOption {
    value: string;
    label: string;
}

/**
 * Per-connection ingestion health, mirrored from telemetry-api's
 * `ConnectionIngestionStatusDto`. Fact-only; the card derives the
 * Connected / Stale / Pending badge from `lastCheckedAt` recency.
 */
export interface ConnectionIngestionStatus {
    providerConnectionId: string;
    lastCheckedAt: string | null;
    lastDate: string | null;
    recordsIngested: number;
    completeness: string | null;
}

/** BFF proxy prefix for telemetry-api (stripped by the upstream router → :3005). */
const TELEMETRY_BASE_PATH = '/_telemetry';
/** BFF proxy prefix for directory-api (stripped by the upstream router → :3003). */
const DIRECTORY_BASE_PATH = '/_directory';

interface DirectorySyncStatusResponse {
    items?: {
        connection: { id: string };
        lastRun: {
            status: string;
            finishedAt: string;
            userCount: number;
        } | null;
    }[];
}

/**
 * Discriminated by which branch the backend took:
 *   - New connection (no existingConnectionId on the state row): returns the
 *     resolved `connectionConfig` and `providerTypeId`; no DB write happened.
 *     The wizard merges these into form state and creates the connection on Save.
 *   - Re-authentication (existingConnectionId set): returns the updated
 *     ProviderConnection with `reauthenticated: true`; the row is already patched.
 */
export interface OauthCallbackResponse {
    providerTypeId?: string;
    connectionConfig?: Record<string, unknown>;
    reauthenticated?: boolean;
    id?: string;
    enabled?: boolean;
    config?: Record<string, unknown>;
}

@injectable()
export class IntegrationsService {
    constructor(
        @inject(ConnectionsClientToken) private readonly client: ConnectionsClient,
        @inject(AuthFetchToken) private readonly authFetch: AuthFetch,
        @inject(ConnectionsApiBasePathToken) private readonly basePath: string,
        @inject(AppBasePathToken) private readonly appBasePath: string,
    ) {}

    /**
     * Absolute URL the OAuth provider redirects to after consent. Built from
     * the SPA's app-base-path so apps mounted under a sub-path (e.g.
     * cloudsaver-hosted bmai-assessment at `/_assessment`) get a redirect
     * URI that resolves to the SPA's `oauth/callback` route.
     */
    oauthCallbackUrl(): string {
        return `${window.location.origin}${this.appBasePath}/oauth/callback`;
    }

    /**
     * List provider types that supply implementations for the given adapter interface.
     * The OpenAPI-generated client returns each row as an unconstrained record because
     * the spec lacks a named ProviderType schema; we cast to the shared shape here.
     * Once the OpenAPI spec is regenerated against a named schema this cast can be removed.
     */
    async listProviderTypes(interfaceName: string): Promise<ProviderType[]> {
        const raw = await this.client.providerTypesApi.getProviderTypes({ _interface: interfaceName });
        return raw as ProviderType[];
    }

    async listConnections(providerTypeId?: string): Promise<ProviderConnection[]> {
        return this.client.providerConnectionsApi.getProviderConnections(
            providerTypeId ? { providerTypeId } : {},
        );
    }

    /**
     * Per-connection ingestion health from telemetry-api. Best-effort — the
     * connection list renders without it, so callers treat a failure as "no
     * status known" rather than a page error.
     */
    async fetchIngestionStatus(): Promise<ConnectionIngestionStatus[]> {
        const response = await this.authFetch(`${TELEMETRY_BASE_PATH}/api/telemetry/connections/ingestion-status`);
        if (!response.ok) {
            throw new Error(`Ingestion status fetch failed (${response.status} ${response.statusText})`);
        }
        const body = (await response.json()) as { statuses?: ConnectionIngestionStatus[] };
        return body.statuses ?? [];
    }

    /**
     * Per-connection directory-sync health from directory-api, mapped to the
     * same {@link ConnectionIngestionStatus} shape the card consumes: last sync
     * time → `lastCheckedAt`, synced user count → `recordsIngested`, run status
     * → `completeness` (`Complete` on success, `failed` otherwise). Best-effort,
     * same as {@link fetchIngestionStatus}.
     */
    async fetchDirectorySyncStatus(): Promise<ConnectionIngestionStatus[]> {
        const response = await this.authFetch(`${DIRECTORY_BASE_PATH}/api/directory/sync/status`);
        if (!response.ok) {
            throw new Error(`Directory sync status fetch failed (${response.status} ${response.statusText})`);
        }
        const body = (await response.json()) as DirectorySyncStatusResponse;
        return (body.items ?? []).map((item) => ({
            providerConnectionId: item.connection.id,
            lastCheckedAt: item.lastRun?.finishedAt ?? null,
            lastDate: null,
            recordsIngested: item.lastRun?.userCount ?? 0,
            completeness: item.lastRun ? (item.lastRun.status === 'success' ? 'Complete' : 'failed') : null,
        }));
    }

    async createConnection(body: CreateBody): Promise<void> {
        await this.client.providerConnectionsApi.postProviderConnections({ createBody: body });
    }

    async updateConnection(id: string, body: UpdateBody): Promise<ProviderConnection> {
        return this.client.providerConnectionsApi.patchById({ id, updateBody: body });
    }

    async deleteConnection(id: string): Promise<void> {
        await this.client.providerConnectionsApi.deleteById({ id });
    }

    async validateConnection(id: string): Promise<ProviderConnectionValidationResult> {
        return this.client.providerConnectionsApi.postByIdValidate({ id });
    }

    async validateConfig(
        providerTypeId: string,
        config: Record<string, unknown>,
    ): Promise<ProviderConnectionValidationResult> {
        return this.client.providerConnectionsApi.postValidateConfig({
            validateConfigBody: { providerTypeId, config },
        });
    }

    async startOauth(startBody: StartBody): Promise<string> {
        const { authUrl } = await this.client.providerConnectionsApi.postOauthStart({ startBody });
        return authUrl;
    }

    /**
     * Start an OAuth flow bound to an existing connection. The callback patches
     * the connection's refresh token (and provider-handler additionalConnectionConfig)
     * instead of creating a new connection. Non-auth fields like matterIds, region,
     * etc. are preserved.
     */
    async startReauth(connectionId: string, reauthStartBody: ReauthStartBody): Promise<string> {
        const { authUrl } = await this.client.providerConnectionsApi.postByIdOauthReauthStart({
            id: connectionId,
            reauthStartBody,
        });
        return authUrl;
    }

    async startAdminConsentOauth(startBody: StartBody): Promise<string> {
        const body = await this.postJson<{ authUrl: string }>(
            `${this.basePath}/api/provider-connections/oauth/admin-consent/start`,
            startBody,
            'OAuth admin-consent start',
        );
        return body.authUrl;
    }

    async completeOauth(callbackBody: CallbackBody): Promise<OauthCallbackResponse> {
        return this.postJson<OauthCallbackResponse>(
            `${this.basePath}/api/provider-connections/oauth/callback`,
            callbackBody,
            'OAuth completion',
        );
    }

    async callProviderTypeRoute<T>(
        providerTypeId: string,
        route: string,
        body: Record<string, unknown>,
    ): Promise<T> {
        return this.postJson<T>(this.buildProviderTypeRouteUrl(providerTypeId, route), body, 'Provider callback');
    }

    async callbackProviderType(
        providerTypeId: string,
        route: string,
        connection: {
            connectionId: string | null;
            providerTypeId: string;
            config: Record<string, unknown>;
            enabled: boolean;
        },
    ): Promise<ProviderOption[]> {
        const data = await this.postJson<unknown>(
            this.buildProviderTypeRouteUrl(providerTypeId, route),
            {
                connectionId: connection.connectionId,
                providerTypeId: connection.providerTypeId,
                config: connection.config,
                enabled: connection.enabled,
            },
            'Provider callback',
        );
        const list = Array.isArray(data)
            ? data
            : Array.isArray((data as { options?: unknown }).options)
                ? (data as { options: unknown[] }).options
                : [];
        return list
            .map((item): ProviderOption | null => {
                if (item && typeof item === 'object') {
                    const o = item as { value?: unknown; label?: unknown };
                    if (typeof o.value === 'string') {
                        return { value: o.value, label: typeof o.label === 'string' ? o.label : o.value };
                    }
                }
                return null;
            })
            .filter((o): o is ProviderOption => o !== null);
    }

    private async postJson<T>(url: string, body: unknown, op: string): Promise<T> {
        const response = await this.authFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(extractErrorMessage(text) || `${op} failed (${response.status} ${response.statusText})`);
        }
        return (await response.json()) as T;
    }

    private buildProviderTypeRouteUrl(providerTypeId: string, route: string): string {
        return `${this.basePath}/api/provider-types/${encodeURIComponent(providerTypeId)}/callback/${encodeURIComponent(route)}`;
    }
}

function extractErrorMessage(rawBody: string): string {
    if (!rawBody) return '';
    try {
        const parsed: unknown = JSON.parse(rawBody);
        if (parsed && typeof parsed === 'object') {
            const obj = parsed as Record<string, unknown>;
            if (typeof obj.error === 'string') return obj.error;
            if (typeof obj.message === 'string') return obj.message;
        }
    } catch {
        return rawBody;
    }
    return rawBody;
}
